'use client';

import React, { useState, Children, cloneElement, isValidElement } from 'react';
import { Check, X, Award } from '@/lib/icons';
import { ButtonCta } from '@/components/ui/ButtonCta';
import { preventOrphans } from '@/lib/typography-utils';
import { TooltipLabel } from '@/components/tools/mortgage-calculator/v3/components/TooltipLabel';

interface PricingFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
  tooltip?: string;
}

interface PricingCardProps {
  title: string;
  price: string;
  originalPrice?: string;
  period?: string;
  description?: string;
  features: (string | PricingFeature)[];
  ctaText?: string;
  ctaHref?: string;
  highlighted?: boolean;
  popular?: boolean;
  badge?: string;
  variant?: 'default' | 'premium' | 'enterprise';
  savings?: string;
  setupFee?: string;
  guarantee?: string;
  className?: string;
  animate?: boolean;
  columnCount?: number;
  titleSizeClass?: string;
  priceSizeClass?: string;
  featureSizeClass?: string;
}

export const PricingCard: React.FC<PricingCardProps> = ({ 
  title, 
  price, 
  originalPrice,
  period = '/month',
  description,
  features,
  ctaText = 'Get Started',
  ctaHref = '/mortgage-lead-generation-demo',
  highlighted = false,
  popular = false,
  badge,
  variant = 'default',
  savings,
  setupFee,
  guarantee,
  className = '',
  animate = true,
  columnCount = 3,
  titleSizeClass,
  priceSizeClass,
  featureSizeClass
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Determine card styling based on variant
  const getCardStyles = () => {
    if (highlighted || popular) {
      return {
        wrapper: 'shadow-elevation-3 scale-[1.02] z-20',
        background: 'bg-white',
        border: 'border-2 border-primary',
        priceColor: 'text-primary'
      };
    }
    
    switch(variant) {
      case 'premium':
        return {
          wrapper: 'shadow-elevation-2 hover:shadow-elevation-3',
          background: 'bg-white',
          border: 'border-2 border-neutral-200',
          priceColor: 'text-neutral-900'
        };
      case 'enterprise':
        return {
          wrapper: 'shadow-elevation-3',
          background: 'bg-neutral-900',
          border: 'border border-neutral-700',
          priceColor: 'text-white',
          textColor: 'text-neutral-100'
        };
      default:
        return {
          wrapper: 'shadow-elevation-1 hover:shadow-elevation-2',
          background: 'bg-white',
          border: 'border border-neutral-200',
          priceColor: 'text-neutral-900'
        };
    }
  };

  const styles = getCardStyles();
  const isEnterprise = variant === 'enterprise';
  
  // Get responsive sizing based on column count - following COMPONENT-VIEWPORT-MATRIX.md
  const getTitleSize = () => {
    // Use passed class or determine from column count
    if (titleSizeClass) return titleSizeClass;
    
    if (columnCount === 1) return 'text-2xl font-bold';
    if (columnCount === 2) return 'text-2xl font-bold';
    if (columnCount === 3) return 'text-xl font-bold';
    return 'text-lg font-bold'; // 4 columns
  };
  
  const getPriceSize = () => {
    // Use passed class or determine from column count
    if (priceSizeClass) return priceSizeClass;
    
    if (columnCount === 1) return 'text-5xl font-black';
    if (columnCount === 2) return 'text-4xl font-black';
    if (columnCount === 3) return 'text-3xl font-black';
    return 'text-2xl font-black'; // 4 columns
  };
  
  const getFeatureSize = () => {
    // Use passed class or determine from column count
    if (featureSizeClass) return featureSizeClass;
    
    if (columnCount === 1) return 'text-base';
    if (columnCount === 2) return 'text-sm';
    if (columnCount === 3) return 'text-xs';
    return 'text-xs'; // 4 columns
  };
  
  // Use configuration-based sizing when columnCount is provided
  const useConfigBased = columnCount && (titleSizeClass || priceSizeClass || featureSizeClass || columnCount !== 3);

  return (
    <div 
      className={`
        pricing-card relative rounded-md overflow-visible
        ${styles.wrapper}
        ${styles.border}
        transform transition-all duration-500 ease-out
        ${animate ? 'hover:scale-[1.02] hover:-translate-y-1' : ''}
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Popular Badge - Outside the card */}
      {(popular || badge) && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-30">
          <div className="bg-primary text-white px-4 py-2 rounded-md text-sm font-semibold shadow-lg whitespace-nowrap">
            {badge || 'Most Popular'}
          </div>
        </div>
      )}

      
      {/* Card Content */}
      <div className={`relative ${styles.background} rounded-md p-4 lg:p-6 h-full flex flex-col`}>
        {/* Header Section */}
        <div className="text-center mb-6">
          {/* Plan Icon */}
          {variant === 'enterprise' && (
            <div className="mb-4 flex justify-center">
              <div className="w-16 h-16 rounded-md bg-gradient-to-br from-neutral-700 to-neutral-800 flex items-center justify-center">
                <Award className="w-8 h-8 text-leadpops-pink" />
              </div>
            </div>
          )}
          
          <h3 
            className={`${useConfigBased ? getTitleSize() : 'text-2xl lg:text-3xl font-bold'} mb-2 text-balance`}
            style={{ color: isEnterprise ? '#ffffff' : undefined }}
          >
            {preventOrphans(title)}
          </h3>
          
          {description && (
            <p 
              className="text-base text-pretty"
              style={{ color: isEnterprise ? '#f5f5f5' : undefined }}
            >
              {preventOrphans(description)}
            </p>
          )}
        </div>
        
        {/* Price Section - PREMIUM */}
        <div className="text-center mb-6">
          {originalPrice && (
            <div className="mb-2">
              <span className={`text-lg line-through ${isEnterprise ? 'text-neutral-400' : 'text-neutral-400'}`}>
                {originalPrice}
              </span>
              {savings && (
                <span className="ml-2 text-sm font-bold text-green-600">
                  Save {savings}
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-baseline justify-center mb-2">
            <span className={`
              ${useConfigBased ? getPriceSize() : 'text-4xl lg:text-5xl font-black'}
              ${styles.priceColor}
              transition-all duration-500
            `}>
              {price}
            </span>
            <span className={`
              ml-2 component-label
              ${isEnterprise ? 'text-neutral-300' : 'text-neutral-600'}
            `}>
              {period}
            </span>
          </div>
          
          {setupFee && (
            <p className={`text-sm ${isEnterprise ? 'text-neutral-300' : 'text-neutral-500'}`}>
              {setupFee} setup fee
            </p>
          )}
        </div>
        
        {/* Features Section */}
        <div className="mb-6 flex-grow">
          <ul className="space-y-3">
            {features
              .sort((a, b) => {
                const aIncluded = typeof a === 'object' ? a.included : true;
                const bIncluded = typeof b === 'object' ? b.included : true;
                if (aIncluded === bIncluded) return 0;
                return aIncluded ? -1 : 1;
              })
              .map((feature, index) => {
              const isObject = typeof feature === 'object';
              const text = isObject ? feature.text : feature;
              const included = isObject ? feature.included : true;
              const isHighlight = isObject && feature.highlight;
              
              return (
                <li 
                  key={index} 
                  className="flex items-start"
                >
                  <div className="mr-2 flex-shrink-0 mt-0.5">
                    {included ? (
                      <Check className={`
                        w-4 h-4
                        ${isHighlight ? 'text-primary' : 
                          isEnterprise ? 'text-neutral-300' : 'text-green-600'}
                      `} />
                    ) : (
                      <X className={`w-4 h-4 ${isEnterprise ? 'text-neutral-600' : 'text-neutral-300'}`} />
                    )}
                  </div>
                  <span className={`
                    flex-1 ${useConfigBased ? getFeatureSize() : 'text-base'} leading-relaxed
                    ${included ? 
                      (isHighlight ? 'font-medium text-primary' : 
                       isEnterprise ? 'text-neutral-100' : 'text-neutral-700') : 
                      (isEnterprise ? 'text-neutral-400 line-through opacity-60' : 'text-neutral-400 line-through opacity-60')
                    }
                  `}>
                    {isObject && feature.tooltip ? (
                      <TooltipLabel 
                        label={preventOrphans(text)}
                        tooltip={feature.tooltip}
                        underline={true}
                      />
                    ) : (
                      preventOrphans(text)
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Guarantee Badge */}
        {guarantee && (
          <div className={`
            text-center mb-6 py-3 px-4 rounded-md
            ${isEnterprise ? 'bg-neutral-800' : 'bg-gradient-to-r from-green-50 to-emerald-50'}
            border ${isEnterprise ? 'border-neutral-700' : 'border-green-200'}
          `}>
            <p className={`
              text-sm font-semibold
              ${isEnterprise ? 'text-neutral-100' : 'text-green-700'}
              flex items-center justify-center gap-2
            `}>
              <Check className="w-4 h-4" />
              {guarantee}
            </p>
          </div>
        )}
        
        {/* CTA Button */}
        <div className="text-center mt-auto">
          <ButtonCta 
            href={ctaHref} 
            variant={highlighted || popular ? 'primary' : 'secondary'}
            className="w-full justify-center"
            showArrow={highlighted || popular}
          >
            {ctaText}
          </ButtonCta>
        </div>
      </div>
    </div>
  );
};

interface PricingGridProps {
  children: React.ReactNode;
  columns?: '2' | '3' | '4';
  className?: string;
}

export const PricingGrid: React.FC<PricingGridProps> = ({ 
  children, 
  columns = '3',
  className = ''
}) => {
  // Convert columns to number for calculations
  const columnCount = parseInt(columns, 10);
  
  // Get configuration-based sizing classes for PricingCard children
  const getTitleSizeClass = () => {
    if (columnCount === 1) return 'text-2xl font-bold';
    if (columnCount === 2) return 'text-2xl font-bold';
    if (columnCount === 3) return 'text-xl font-bold';
    return 'text-lg font-bold'; // 4 columns
  };
  
  const getPriceSizeClass = () => {
    if (columnCount === 1) return 'text-5xl font-black';
    if (columnCount === 2) return 'text-4xl font-black';
    if (columnCount === 3) return 'text-3xl font-black';
    return 'text-2xl font-black'; // 4 columns
  };
  
  const getFeatureSizeClass = () => {
    if (columnCount === 1) return 'text-base';
    if (columnCount === 2) return 'text-sm';
    if (columnCount === 3) return 'text-xs';
    return 'text-xs'; // 4 columns
  };
  
  const titleSizeClass = getTitleSizeClass();
  const priceSizeClass = getPriceSizeClass();
  const featureSizeClass = getFeatureSizeClass();
  
  // Add configuration data to PricingCard children
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child) && child.type === PricingCard) {
      return cloneElement(child as React.ReactElement<any>, {
        ...child.props,
        columnCount: columnCount,
        titleSizeClass: titleSizeClass,
        priceSizeClass: priceSizeClass,
        featureSizeClass: featureSizeClass
      });
    }
    return child;
  });
  const gridClasses = {
    '2': 'grid-cols-1 lg:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
    '4': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  // Break out of content constraints for 3-column pricing
  const useFullWidth = columns === '3';
  
  if (useFullWidth) {
    return (
      <div 
        className="my-12" 
        style={{
          marginLeft: 'calc(50% - 50vw)',
          marginRight: 'calc(50% - 50vw)',
          paddingLeft: 'max(1rem, calc(50vw - 42rem))',
          paddingRight: 'max(1rem, calc(50vw - 42rem))'
        }}
      >
        <div className={`
          grid ${gridClasses[columns]} gap-6 lg:gap-8
          items-stretch
          ${className}
        `}>
          {enhancedChildren}
        </div>
      </div>
    );
  }

  return (
    <div className={`
      grid ${gridClasses[columns]} gap-8 lg:gap-10
      my-12 items-stretch
      ${className}
    `}>
      {enhancedChildren}
    </div>
  );
};

